import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Calendar, Trash2, Eye } from 'lucide-react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ChatHistory {
  id: number;
  user_id: number;
  conversation_id: string;
  message_type: string;
  message_content: string;
  timestamp: string;
  context_data: string;
}

interface ConversationSummary {
  conversation_id: string;
  title: string;
  last_message: string;
  last_timestamp: string;
  message_count: number;
  preview: string;
}

interface ConversationHistoryProps {
  onSelectConversation?: (conversationId: string) => void;
  selectedConversationId?: string;
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  onSelectConversation,
  selectedConversationId
}) => {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadConversationHistory();
    }
  }, [user]);

  const loadConversationHistory = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      // Fetch all chat history for the user
      const { data, error } = await window.ezsite.apis.tablePage('15991', {
        PageNo: 1,
        PageSize: 1000, // Get all conversations
        OrderByField: 'timestamp',
        IsAsc: false,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: user.ID
        }]

      });

      if (error) {
        throw new Error(error);
      }

      const chatHistory: ChatHistory[] = data?.List || [];

      // Group messages by conversation_id and create summaries
      const conversationMap = new Map<string, ChatHistory[]>();

      chatHistory.forEach((message) => {
        if (!conversationMap.has(message.conversation_id)) {
          conversationMap.set(message.conversation_id, []);
        }
        conversationMap.get(message.conversation_id)!.push(message);
      });

      // Create conversation summaries
      const summaries: ConversationSummary[] = [];

      conversationMap.forEach((messages, conversationId) => {
        // Sort messages by timestamp
        messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        const firstUserMessage = messages.find((m) => m.message_type === 'user');
        const lastMessage = messages[messages.length - 1];

        // Generate a title from the first user message or use a default
        let title = 'New Conversation';
        if (firstUserMessage) {
          title = firstUserMessage.message_content.length > 50 ?
          firstUserMessage.message_content.substring(0, 50) + '...' :
          firstUserMessage.message_content;
        }

        // Create preview from last few messages
        const recentMessages = messages.slice(-3);
        const preview = recentMessages.
        map((m) => `${m.message_type === 'user' ? 'You' : 'AI'}: ${m.message_content.substring(0, 100)}`).
        join(' | ');

        summaries.push({
          conversation_id: conversationId,
          title,
          last_message: lastMessage.message_content,
          last_timestamp: lastMessage.timestamp,
          message_count: messages.length,
          preview: preview.length > 200 ? preview.substring(0, 200) + '...' : preview
        });
      });

      // Sort by last timestamp (most recent first)
      summaries.sort((a, b) => new Date(b.last_timestamp).getTime() - new Date(a.last_timestamp).getTime());

      setConversations(summaries);

    } catch (error) {
      console.error('Error loading conversation history:', error);
      toast({
        title: "Error Loading History",
        description: "Failed to load conversation history. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteConversation = async (conversationId: string) => {
    if (!user || !confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      // Get all messages in this conversation
      const { data, error } = await window.ezsite.apis.tablePage('15991', {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'timestamp',
        IsAsc: true,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: user.ID
        },
        {
          name: 'conversation_id',
          op: 'Equal',
          value: conversationId
        }]

      });

      if (error) {
        throw new Error(error);
      }

      const messages: ChatHistory[] = data?.List || [];

      // Delete all messages in the conversation
      for (const message of messages) {
        const { error: deleteError } = await window.ezsite.apis.tableDelete('15991', {
          ID: message.id
        });

        if (deleteError) {
          console.error('Error deleting message:', deleteError);
        }
      }

      // Refresh the conversation list
      await loadConversationHistory();

      toast({
        title: "Conversation Deleted",
        description: "The conversation has been successfully deleted."
      });

    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast({
        title: "Error Deleting Conversation",
        description: "Failed to delete the conversation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (isLoading) {
    return (
      <Card className="h-full" data-id="x5p1dlkab" data-path="src/components/ai/ConversationHistory.tsx">
        <CardHeader data-id="b9ihhcp3a" data-path="src/components/ai/ConversationHistory.tsx">
          <CardTitle className="flex items-center gap-2" data-id="0jlcl07qa" data-path="src/components/ai/ConversationHistory.tsx">
            <MessageCircle className="h-5 w-5" data-id="uw06k0grz" data-path="src/components/ai/ConversationHistory.tsx" />
            Loading Conversations...
          </CardTitle>
        </CardHeader>
        <CardContent data-id="krl2fbs79" data-path="src/components/ai/ConversationHistory.tsx">
          <div className="space-y-4" data-id="7ntz3a5oo" data-path="src/components/ai/ConversationHistory.tsx">
            {[1, 2, 3].map((i) =>
            <div key={i} className="animate-pulse" data-id="z46gk4o7k" data-path="src/components/ai/ConversationHistory.tsx">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" data-id="7vrkdr2iu" data-path="src/components/ai/ConversationHistory.tsx"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2" data-id="a8zwvqi5l" data-path="src/components/ai/ConversationHistory.tsx"></div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>);

  }

  return (
    <Card className="h-full" data-id="qopkuw6e9" data-path="src/components/ai/ConversationHistory.tsx">
      <CardHeader data-id="fk8w90t9f" data-path="src/components/ai/ConversationHistory.tsx">
        <CardTitle className="flex items-center gap-2" data-id="51k3z6rl1" data-path="src/components/ai/ConversationHistory.tsx">
          <MessageCircle className="h-5 w-5" data-id="qf76dyjui" data-path="src/components/ai/ConversationHistory.tsx" />
          Conversation History
          <Badge variant="secondary" className="ml-auto" data-id="js99jjjd4" data-path="src/components/ai/ConversationHistory.tsx">
            {conversations.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0" data-id="nveu5frbt" data-path="src/components/ai/ConversationHistory.tsx">
        <ScrollArea className="h-[600px]" data-id="2e2pgjkcj" data-path="src/components/ai/ConversationHistory.tsx">
          {conversations.length === 0 ?
          <div className="p-6 text-center text-gray-500" data-id="8pdpdqee6" data-path="src/components/ai/ConversationHistory.tsx">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" data-id="ldtrmafz9" data-path="src/components/ai/ConversationHistory.tsx" />
              <p className="text-lg font-medium mb-2" data-id="e0b9q1y96" data-path="src/components/ai/ConversationHistory.tsx">No conversations yet</p>
              <p className="text-sm" data-id="a7guh7ubs" data-path="src/components/ai/ConversationHistory.tsx">Start chatting with the AI assistant to see your conversation history here.</p>
            </div> :

          <div className="space-y-2 p-4" data-id="x7ij2n5h7" data-path="src/components/ai/ConversationHistory.tsx">
              {conversations.map((conversation) =>
            <motion.div
              key={conversation.conversation_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-md ${
              selectedConversationId === conversation.conversation_id ?
              'border-indigo-500 bg-indigo-50' :
              'border-gray-200 hover:border-gray-300'}`
              }
              onClick={() => onSelectConversation?.(conversation.conversation_id)} data-id="facu10aak" data-path="src/components/ai/ConversationHistory.tsx">

                  <div className="flex items-start justify-between mb-2" data-id="24xbr8qs6" data-path="src/components/ai/ConversationHistory.tsx">
                    <h3 className="font-medium text-gray-900 line-clamp-2 flex-1" data-id="lfoolaqln" data-path="src/components/ai/ConversationHistory.tsx">
                      {conversation.title}
                    </h3>
                    <div className="flex items-center gap-2 ml-2" data-id="opqizx9yo" data-path="src/components/ai/ConversationHistory.tsx">
                      <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectConversation?.(conversation.conversation_id);
                    }}
                    className="h-8 w-8 p-0" data-id="kh94zt1dz" data-path="src/components/ai/ConversationHistory.tsx">

                        <Eye className="h-4 w-4" data-id="2hkkke48r" data-path="src/components/ai/ConversationHistory.tsx" />
                      </Button>
                      <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conversation.conversation_id);
                    }}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700" data-id="c0s369cq8" data-path="src/components/ai/ConversationHistory.tsx">

                        <Trash2 className="h-4 w-4" data-id="mr98icehb" data-path="src/components/ai/ConversationHistory.tsx" />
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3" data-id="9fl4xeawn" data-path="src/components/ai/ConversationHistory.tsx">
                    {conversation.preview}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500" data-id="sgxenr2id" data-path="src/components/ai/ConversationHistory.tsx">
                    <div className="flex items-center gap-4" data-id="zvkt4q0ve" data-path="src/components/ai/ConversationHistory.tsx">
                      <span className="flex items-center gap-1" data-id="bdjf44vp9" data-path="src/components/ai/ConversationHistory.tsx">
                        <Calendar className="h-3 w-3" data-id="huceao4tb" data-path="src/components/ai/ConversationHistory.tsx" />
                        {formatTimestamp(conversation.last_timestamp)}
                      </span>
                      <Badge variant="outline" className="text-xs" data-id="p452bwasu" data-path="src/components/ai/ConversationHistory.tsx">
                        {conversation.message_count} messages
                      </Badge>
                    </div>
                  </div>
                </motion.div>
            )}
            </div>
          }
        </ScrollArea>
      </CardContent>
    </Card>);

};

export default ConversationHistory;